import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Subscription, firstValueFrom, interval } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { ConfigService } from './config.service.js';
import { environment } from '../../environments/environment.js';
import { AuthService } from '../auth/auth.service.js';
import { DigitalTwinFeature, type DigitalTwinGeometry } from '../types/digital-twin.js';

export interface ModelMetadata {
  modelId: string;
  tenant: string;
  saPath: string | null;
  coordinateSystem: string | null;
  format: string | null;
}

export { DigitalTwinFeature, DigitalTwinGeometry };

/**
 * Shared API layer for point cloud model metadata and token management.
 * Used by both ViewerService (direct modelId load) and DynamicPointCloudService (proximity load).
 */
@Injectable({ providedIn: 'root' })
export class PointCloudApiService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private config = inject(ConfigService);
  private tokenRefreshSubscription: Subscription | null = null;

  async fetchModelMetadata(tenant: string, modelId: string): Promise<ModelMetadata> {
    const result = (await firstValueFrom(
      this.http.get(`${environment.apiUrl}/digital_twin/${modelId}?tenant=${tenant}`),
    )) as any;
    return {
      modelId,
      tenant,
      saPath: result['url'],
      coordinateSystem: result['coordinateSystem'],
      format: result['fileFormat'],
    };
  }

  buildModelUrl(saPath: string, format: string | null): string {
    if (format == 'copc.laz') {
      return saPath;
    }
    if (format === 'entwine') {
      return `${saPath}/ept.json`;
    }
    return `${saPath}/metadata.json`;
  }

  /**
   * Fetch all digital twin point cloud models for a tenant as GeoJSON
   * features of any geometry type (Point, LineString, Polygon, …), looping
   * over every page of the paginated API.
   */
  async fetchDigitalTwinFeatures(tenant: string): Promise<DigitalTwinFeature[]> {
    const features: DigitalTwinFeature[] = [];
    const size = 50;
    let page = 1;
    let totalPages = 1;

    do {
      const url = `${environment.apiUrl}/digital_twin?tenant=${tenant}&page=${page}&size=${size}&viewer=pointcloud`;
      const response = (await firstValueFrom(this.http.get(url))) as any;

      const items: any[] = Array.isArray(response?.items) ? response.items : [];
      for (const item of items) {
        const geometry = item?.geoData?.geometry;
        if (geometry && typeof geometry.type === 'string' && geometry.coordinates != null) {
          features.push({
            guid: item.guid,
            name: item.name ?? '',
            geometry: {
              type: geometry.type,
              coordinates: geometry.coordinates,
            },
          });
        }
      }

      totalPages = typeof response?.pages === 'number' && response.pages > 0 ? response.pages : 1;
      page++;
    } while (page <= totalPages);

    return features;
  }

  async fetchToken(tenant: string, modelId: string): Promise<string | null> {
    try {
      const token = await firstValueFrom(
        this.http.get(
          `${environment.apiUrl}/digital_twin/pointcloud/${modelId}/token?tenant=${tenant}`,
          { responseType: 'text' },
        ),
      );
      return token;
    } catch (error) {
      console.error('Failed to fetch token:', error);
      return null;
    }
  }

  /**
   * Fetch the token and store it in ConfigService along with the access token.
   * Returns the token or null on failure.
   */
  async activateToken(tenant: string, modelId: string): Promise<string | null> {
    const token = await this.fetchToken(tenant, modelId);
    if (!token) return null;

    this.config.updateSasToken(token);
    const accessToken = await firstValueFrom(this.authService.accessToken$);
    this.config.updateAccessToken(accessToken);
    return token;
  }

  /**
   * Start periodic SAS token refresh (every 5 minutes).
   * Stops any previously running refresh.
   */
  startTokenRefresh(tenant: string, modelId: string): void {
    this.stopTokenRefresh();
    this.tokenRefreshSubscription = interval(300000)
      .pipe(
        switchMap(() =>
          this.http.get(
            `${environment.apiUrl}/digital_twin/pointcloud/${modelId}/token?tenant=${tenant}`,
            { responseType: 'text' },
          ),
        ),
        catchError(error => {
          console.error('Token refresh failed:', error);
          return [];
        }),
      )
      .subscribe(async token => {
        if (token && typeof token === 'string') {
          this.config.updateSasToken(token);
          const accessToken = await firstValueFrom(this.authService.accessToken$);
          this.config.updateAccessToken(accessToken);
        }
      });
  }

  stopTokenRefresh(): void {
    if (this.tokenRefreshSubscription) {
      this.tokenRefreshSubscription.unsubscribe();
      this.tokenRefreshSubscription = null;
    }
  }
}
